import { RpcError } from '@protobuf-ts/runtime-rpc'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { BaseActionForm } from 'components/pages/task/action/form/BaseActionForm'
import { getApproveText, getSuccessMessage, getApprovePresets, getCertifyPresets, getConfirmPresets } from 'components/pages/task/action/messages'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { FC, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

type ApproveWithCommentTaskDialog = {
    task: TaskValue
    useStepUp?: boolean
} & ClosableProps

export const ApproveWithCommentTaskDialog: FC<ApproveWithCommentTaskDialog> = ({ task, close, useStepUp = false }) => {
    const intl = useIntl()
    const toast = useAlert()
    const ticketActionTracker = useTicketActionTracker()

    const getLabels = useCallback((): { title: string; description: string, presets: string[] } => {
        switch (task.type?.taskType.oneofKind) {
            case 'certify':
                return {
                    title: intl.formatMessage({
                        id: 'component_action_form_certify_title',
                        defaultMessage: 'Certify access with comment',
                    }),
                    description: intl.formatMessage({
                        id: 'component_action_form_certify_description',
                        defaultMessage: 'Add your comment and certify this access.',
                    }),
                    presets: getCertifyPresets(intl),
                }
            case 'revoke':
                return {
                    title: intl.formatMessage({
                        id: 'component_action_form_revoke_title',
                        defaultMessage: 'Confirm with comment',
                    }),
                    description: intl.formatMessage({
                        id: 'component_action_form_revoke_description',
                        defaultMessage: 'Add your comment and confirm removal of this access.',
                    }),
                    presets: getConfirmPresets(intl),
                }
            default:
                // This is more generic
                return {
                    title: intl.formatMessage({
                        id: 'component_action_form_grant_title',
                        defaultMessage: 'Approve access with comment',
                    }),
                    description: intl.formatMessage({
                        id: 'component_action_form_grant_description',
                        defaultMessage: 'Add your comment and approve this access.',
                    }),
                    presets: getApprovePresets(intl),
                }
        }
    }, [intl, task.type?.taskType.oneofKind])

    const handleSave = useCallback(
        async (comment: string): Promise<void> => {
            // This is a placeholder function for initiating the step-up flow
            // In a real implementation, this would show a step-up authentication UI
            // and return a stepUpTransactionId
            const initiateStepUpFlow = async (): Promise<string> => {
                // This is just a placeholder - in a real scenario, this would be replaced
                // with an actual step-up authentication flow implementation
                throw new Error('Step-up authentication flow not implemented')
            }

            try {
                if (useStepUp) {
                    // In a real implementation, we would need to initiate a step-up flow here
                    // This is a placeholder - in a real scenario we would need to get the stepUpTransactionId
                    // from a separate step-up authentication flow
                    const stepUpTransactionId = await initiateStepUpFlow()
                    await ticketActionTracker?.approveWithStepUp(task, comment, stepUpTransactionId)
                } else {
                    await ticketActionTracker?.approve(task, comment)
                }
            } catch (err: unknown) {
                const errorMessage = useStepUp
                    ? intl.formatMessage(
                        {
                            id: 'task_approve_with_step_up_error',
                            defaultMessage: 'Error approving task with step-up authentication. {code}: {msg}',
                        },
                        {
                            code: (err as RpcError).code,
                            msg: (err as RpcError).message,
                        },
                    )
                    : intl.formatMessage(
                        {
                            id: 'task_approve_error',
                            defaultMessage: 'Error approving task. {code}: {msg}',
                        },
                        {
                            code: (err as RpcError).code,
                            msg: (err as RpcError).message,
                        },
                    )

                return toast(errorMessage, 'error')
            }

            toast(
                getSuccessMessage({
                    actionType: 'approve',
                    intl,
                    task,
                }),
                'success',
            )

            close()
        },
        [toast, intl, task, ticketActionTracker, close, useStepUp],
    )

    return (
        <BaseActionForm
            closeDialog={close}
            open
            onApprove={handleSave}
            title={getLabels().title}
            description={getLabels().description}
            presets={getLabels().presets}
            actionText={getApproveText(intl, task)}
            isActionLoading={!ticketActionTracker}
        />
    )
}
